import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, Mail, Globe } from 'lucide-react';
import SITE_CONFIG from '../config/siteConfig';

const PrivacyPage = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - AI Carousel Maker</title>
        <link rel="canonical" href={`${SITE_CONFIG.siteUrl}/privacy`} />
        <meta name="description" content="Privacy Policy for AI Carousel Maker. Learn how we collect, use, and protect your personal information." />
        <meta property="og:title" content="Privacy Policy - AI Carousel Maker" />
        <meta property="og:url" content={`${SITE_CONFIG.siteUrl}/privacy`} />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Shield className="h-12 w-12 text-primary-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-600">Last updated: 12 December 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>NovialCraft</strong> ("we", "our", or "us") operates the website{' '}
              <a href="https://novialcraft.com" className="text-primary-600 hover:text-primary-700">
                https://novialcraft.com
              </a>
              , related subdomains including{' '}
              <a href="https://api.novialcraft.com" className="text-primary-600 hover:text-primary-700">
                https://api.novialcraft.com
              </a>
              , and associated services (collectively, the "Service").
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              This Privacy Policy explains how we collect, use, store, and protect your information when you use our platform.
            </p>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.1 Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">We may collect:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Account credentials (encrypted)</li>
                <li>Billing information (processed via third-party payment providers)</li>
                <li>Contact information provided by you</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Note:</strong> We do not store raw card or payment details on our servers.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.2 User-Generated Content</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Images, prompts, and files you generate or upload</li>
                <li>Metadata related to generated content</li>
                <li>Usage history related to your account</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">1.3 Technical & Usage Data</h3>
              <p className="text-gray-700 leading-relaxed mb-3">Automatically collected:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>IP address</li>
                <li>Browser type and device information</li>
                <li>Operating system</li>
                <li>Access timestamps</li>
                <li>API usage logs</li>
                <li>Cookies and similar technologies</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">We use collected data to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide and operate the Service</li>
                <li>Generate and store AI-created content</li>
                <li>Authenticate users and secure accounts</li>
                <li>Process payments and subscriptions</li>
                <li>Improve performance and reliability</li>
                <li>Detect fraud, abuse, and security issues</li>
                <li>Communicate service updates or support messages</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Cookies & Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-3">We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Maintain login sessions</li>
                <li>Improve user experience</li>
                <li>Analyze usage and performance</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                You may disable cookies via browser settings, but some features may not work correctly.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Storage & Data Processing</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>User data and generated content may be stored on cloud infrastructure, including third-party storage providers.</li>
                <li>Data may be processed in different geographic locations depending on infrastructure availability.</li>
                <li>We apply reasonable technical and organizational measures to protect your data.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Sharing</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>We do not sell your personal data.</strong>
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">We may share data with:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Payment processors</li>
                <li>Cloud infrastructure providers</li>
                <li>Analytics and monitoring services</li>
                <li>Legal authorities when required by law</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                All third parties are required to protect your information.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>We retain personal data as long as your account is active or as required to provide services.</li>
                <li>Generated content may be deleted after account termination or inactivity.</li>
                <li>Some data may be retained for legal, security, or compliance purposes.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. User Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Depending on your jurisdiction, you may have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Access your personal data</li>
                <li>Request correction or deletion</li>
                <li>Withdraw consent</li>
                <li>Request data portability</li>
                <li>Object to processing</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                You may exercise these rights by contacting us.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Account Deletion</h2>
              <p className="text-gray-700 leading-relaxed mb-3">If you request account deletion:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Your personal data will be deleted or anonymized where possible</li>
                <li>Generated content may be permanently removed</li>
                <li>Some records may be retained for legal compliance</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Security Measures</h2>
              <p className="text-gray-700 leading-relaxed mb-3">We implement:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                <li>Encrypted communication (HTTPS)</li>
                <li>Access control and authentication</li>
                <li>Secure server configurations</li>
                <li>Regular monitoring and updates</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                However, no system is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Our services are not intended for individuals under 18.</li>
                <li>We do not knowingly collect personal data from children.</li>
              </ul>
            </section>

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Third-Party Links</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Our platform may contain links to third-party websites.</li>
                <li>We are not responsible for their privacy practices.</li>
              </ul>
            </section>

            {/* Section 12 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Policy</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>We may update this Privacy Policy from time to time.</li>
                <li>Changes will be posted on this page with an updated date.</li>
              </ul>
            </section>

            {/* Section 13 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions or concerns about this Privacy Policy, contact us at:
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-primary-600" />
                  <a href="mailto:support@novialtechnologies.com" className="text-primary-600 hover:text-primary-700">
                    support@novialtechnologies.com
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Globe className="h-5 w-5 text-primary-600" />
                  <a href="https://novialcraft.com" className="text-primary-600 hover:text-primary-700">
                    https://novialcraft.com
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link 
            to="/" 
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default PrivacyPage;
